import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { v4 as uuidv4 } from 'uuid';

export interface ErrorToastRecord {
  id: string;
  title?: string;
  message: string;
  source?: string;
  severity: 'error' | 'warning';
  createdAt: number;
}

export type ErrorToastInput = {
  title?: string;
  message: string;
  source?: string;
  severity?: 'error' | 'warning';
};

interface ErrorToastState {
  errors: ErrorToastRecord[];
}

type ErrorToastAction =
  | { type: 'SHOW_ERROR'; payload: ErrorToastInput }
  | { type: 'DISMISS_ERROR'; id: string }
  | { type: 'CLEAR_ERRORS' };

const DEDUPE_WINDOW_MS = 5_000;
const MAX_TOASTS = 3;

function errorToastReducer(state: ErrorToastState, action: ErrorToastAction): ErrorToastState {
  switch (action.type) {
    case 'SHOW_ERROR': {
      const now = Date.now();
      const isDuplicate = state.errors.some(
        (error) =>
          error.message === action.payload.message &&
          error.source === action.payload.source &&
          now - error.createdAt < DEDUPE_WINDOW_MS
      );
      if (isDuplicate) return state;

      const nextError: ErrorToastRecord = {
        id: uuidv4(),
        title: action.payload.title,
        message: action.payload.message,
        source: action.payload.source,
        severity: action.payload.severity ?? 'error',
        createdAt: now,
      };

      const errors = [...state.errors, nextError];
      if (errors.length > MAX_TOASTS) {
        errors.shift();
      }
      return { errors };
    }
    case 'DISMISS_ERROR':
      return { errors: state.errors.filter((error) => error.id !== action.id) };
    case 'CLEAR_ERRORS':
      return { errors: [] };
    default:
      return state;
  }
}

interface ErrorToastContextValue {
  showError: (input: ErrorToastInput) => void;
  dismissError: (id: string) => void;
  clearErrors: () => void;
  errors: ErrorToastRecord[];
}

const ErrorToastContext = createContext<ErrorToastContextValue | null>(null);

export function ErrorToastProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(errorToastReducer, { errors: [] });

  const showError = useCallback((input: ErrorToastInput) => {
    dispatch({ type: 'SHOW_ERROR', payload: input });
  }, []);

  const dismissError = useCallback((id: string) => {
    dispatch({ type: 'DISMISS_ERROR', id });
  }, []);

  const clearErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' });
  }, []);

  const value = useMemo(
    () => ({
      showError,
      dismissError,
      clearErrors,
      errors: state.errors,
    }),
    [clearErrors, dismissError, showError, state.errors]
  );

  return <ErrorToastContext.Provider value={value}>{children}</ErrorToastContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useErrorToast(): ErrorToastContextValue {
  const context = useContext(ErrorToastContext);
  if (!context) {
    throw new Error('useErrorToast must be used within ErrorToastProvider');
  }
  return context;
}
